/**
 * BgJobsTray · indicatore globale flottante delle generazioni in background.
 *
 * Si posiziona in basso a sinistra (lato opposto rispetto al PackJobsTray
 * che sta a destra) per non sovrapporsi. Su mobile resta sopra la
 * BottomTabBar (height ~64px + safe-area).
 *
 * Stati per job:
 *   • running → spinner + tempo trascorso (mm:ss)
 *   • done    → check verde (auto-dismiss dopo 6s)
 *   • failed  → triangolo (auto-dismiss dopo 12s)
 *
 * Si nasconde completamente se non ci sono job.
 */
import React, { useEffect, useReducer, useState } from "react";
import { CheckCircle, Warning, X } from "@phosphor-icons/react";
import { useBgJobs } from "@/components/jobs/useBgJobs";
import { isNative } from "@/lib/native";

const TICK_MS = 1000;

export default function BgJobsTray() {
  const { jobs, remove } = useBgJobs();
  const [collapsed, setCollapsed] = useState(false);
  // Tick ogni secondo SOLO se ci sono job running, per aggiornare l'elapsed
  const hasRunning = jobs.some((j) => j.status === "running");
  const [, forceTick] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    if (!hasRunning) return;
    const id = setInterval(forceTick, TICK_MS);
    return () => clearInterval(id);
  }, [hasRunning]);

  if (!jobs || jobs.length === 0) return null;

  const running = jobs.filter((j) => j.status === "running").length;
  const done = jobs.filter((j) => j.status === "done").length;
  const failed = jobs.filter((j) => j.status === "failed").length;

  return (
    <div
      className="fixed left-4 z-[60] w-[280px] max-w-[calc(100vw-2rem)] rounded-lg border border-[#1C1C1A]/15 bg-white/95 backdrop-blur-md shadow-lg shadow-black/10 overflow-hidden"
      data-testid="bg-jobs-tray"
      style={{
        // Su mobile native iOS la BottomTabBar è 80px + safe-area inset bottom.
        // Su web/desktop la bottom-nav non c'è ma teniamo offset omogeneo (16px).
        bottom: isNative
          ? "calc(80px + env(safe-area-inset-bottom) + 8px)"
          : "16px",
      }}
    >
      <button
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center justify-between px-3 py-2 bg-[#1C1C1A] text-white text-[12px] font-semibold hover:bg-[#2A2A29] transition-colors"
        data-testid="bg-jobs-tray-toggle"
      >
        <div className="flex items-center gap-2">
          {running > 0 && (
            <span
              className="inline-block w-2 h-2 rounded-full bg-[#B5C4A6] animate-pulse"
              aria-hidden="true"
            />
          )}
          {running > 0 && (
            <span>
              {running === 1
                ? "1 in lavorazione"
                : `${running} in lavorazione`}
            </span>
          )}
          {done > 0 && running === 0 && (
            <span className="text-[#B5C4A6]">
              {done === 1 ? "1 pronto" : `${done} pronti`}
            </span>
          )}
          {failed > 0 && (
            <span className="text-[#E5A48E]">
              {running > 0 || done > 0 ? "·" : ""} {failed}{" "}
              {failed === 1 ? "errore" : "errori"}
            </span>
          )}
        </div>
        <span className="text-[10px] text-white/55">{collapsed ? "▾" : "▴"}</span>
      </button>

      {!collapsed && (
        <div className="divide-y divide-[#EAE7DF] max-h-[280px] overflow-y-auto">
          {jobs.map((job) => (
            <JobRow
              key={job.id}
              job={job}
              onDismiss={() => remove(job.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function JobRow({ job, onDismiss }) {
  const isRunning = job.status === "running";
  const isDone = job.status === "done";
  const isFailed = job.status === "failed";
  const elapsed = _elapsedSeconds(job.started_at);
  const elapsedLbl =
    elapsed >= 60
      ? `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, "0")}`
      : `${elapsed}s`;

  return (
    <div
      className="px-3 py-2.5 flex items-start gap-2"
      data-testid={`bg-job-${job.id}`}
    >
      <div className="shrink-0 mt-0.5">
        {isRunning && (
          <span
            className="inline-block w-3.5 h-3.5 border-2 border-[#7C8B6E] border-t-transparent rounded-full animate-spin"
            aria-hidden="true"
          />
        )}
        {isDone && (
          <CheckCircle size={16} weight="fill" className="text-[#7C8B6E]" />
        )}
        {isFailed && (
          <Warning size={16} weight="fill" className="text-[#C4734A]" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="text-[12px] font-semibold text-[#1C1C1A] truncate">
          {job.label}
        </div>
        <div className="text-[10.5px] text-[#7C7B73] mt-0.5">
          {isRunning && <>In lavorazione · {elapsedLbl}</>}
          {isDone && "Pronto"}
          {isFailed && (job.error ? `Errore · ${job.error}` : "Errore")}
        </div>
      </div>

      <div className="shrink-0 flex items-center gap-1">
        {(isDone || isFailed) && (
          <button
            onClick={onDismiss}
            className="p-1 rounded hover:bg-[#EAE7DF] text-[#7C7B73]"
            aria-label="Chiudi"
            data-testid={`bg-job-dismiss-${job.id}`}
          >
            <X size={11} weight="bold" />
          </button>
        )}
      </div>
    </div>
  );
}

function _elapsedSeconds(ts) {
  if (!ts) return 0;
  return Math.max(0, Math.floor((Date.now() - ts) / 1000));
}
